import { useEffect, useRef } from 'react';
import { prefillVmLevelFields } from '../components/VMBasedMigrationsConfig/helpers';
import { handleError } from '../../../../lib/helpers';
import { getOutpostInstanceTypes, getOutpostInstanceType } from './useAWSApplianceSetup';
import { useNotificationContext } from '../../../../providers';

// Dedicated host tenancy is hidden in AWSApplianceSetup.jsx (hardcoded shared tenancy), so redeploy should not
// restore a previous appliance's dedicated-host tenancy onto the hidden field. Keep this in sync with
// ALLOW_DEDICATED_HOST_DEPLOYMNET in AWSApplianceSetup.jsx.
const ALLOW_DEDICATED_HOST_DEPLOYMNET = false;

const restoreEncryption = (props, regionData, form) => {
    if (!props.encrypt_volumes) {
        return;
    }
    if (!props.encryption_key_arn) {
        form.setFieldsValue({ encryptionType: 'default_key' });
        return;
    }
    const matchedKey = (regionData.kms_keys || []).find((key) => key.arn === props.encryption_key_arn);
    const encryptionType = matchedKey?.key_manager === 'AWS' ? 'platform_key' : 'customer_key';
    form.setFieldsValue({ encryptionType, kmsKey: props.encryption_key_arn });
};

const restoreVpcAndSubnets = (props, regionData, form, setSubnets) => {
    if (!props.vpc) {
        return [];
    }
    form.setFieldsValue({ vpc: props.vpc });
    const vpc = (regionData.vpcs || []).find((candidate) => candidate.id === props.vpc);
    const subnets = vpc?.subnets || [];
    if (vpc) {
        setSubnets(subnets);
    }
    return subnets;
};

const restoreOutpostInstanceType = (props, regionData, form, subnets) => {
    const subnetId = props.network?.interfaces?.eth0?.network_name;
    const instanceTypes = getOutpostInstanceTypes(subnetId, subnets, regionData?.outposts);
    if (instanceTypes) {
        form.setFieldsValue({ instanceType: getOutpostInstanceType(instanceTypes, props.instance_type) });
    }
};

const restoreTenancy = (props, form) => {
    if (!ALLOW_DEDICATED_HOST_DEPLOYMNET || props.tenancy !== 'host') {
        return;
    }
    form.setFieldsValue({
        tenancy: 'host',
        hostId: props.host_id || 'default',
        affinity: props.affinity || 'host',
    });
};

const restoreFsx = (props, form) => {
    if (!Array.isArray(props.aws_fsx) || props.aws_fsx.length === 0) {
        return;
    }
    const filesystem = props.aws_fsx[0];
    form.setFieldsValue({
        enableFsx: true,
        fsxFilesystemId: filesystem.id,
        fsxUsername: filesystem.credentials?.username || '',
        fsxPassword: '',
    });
};

const loadRedeployRegionData = async ({
    appliance,
    cloudAccount,
    form,
    fetchAttributes,
    notifyError,
    setLoadingRegionData,
    setVPCs,
    setSubnets,
    setKmsKeys,
    setDedicatedHosts,
    setFsxFilesystems,
    setOutposts,
}) => {
    const props = appliance.cloud_properties;
    form.setFieldsValue({ region: props.region });

    setLoadingRegionData(true);
    try {
        const result = await fetchAttributes({
            cloudAccountId: cloudAccount.id,
            params: { region: props.region },
        }).unwrap();

        const regionData = result?.properties?.regions?.[0];
        if (!regionData) {
            return;
        }

        setVPCs(regionData.vpcs || []);
        setKmsKeys(regionData.kms_keys || []);
        setDedicatedHosts(regionData.hosts || []);
        setFsxFilesystems(regionData.fsx_filesystems || []);
        setOutposts(regionData.outposts || []);

        restoreEncryption(props, regionData, form);
        const subnets = restoreVpcAndSubnets(props, regionData, form, setSubnets);
        restoreOutpostInstanceType(props, regionData, form, subnets);
        restoreTenancy(props, form);
        restoreFsx(props, form);
    } catch (error) {
        handleError(error, notifyError, 'Failed to load region attributes');
    } finally {
        setLoadingRegionData(false);
    }
};

export const useApplianceRedeployPrefill = ({
    isRedeploy,
    cloudAccount,
    regions,
    form,
    fetchAttributes,
    prefillCommonFromAppliance,
    setVPCs,
    setSubnets,
    setKmsKeys,
    setDedicatedHosts,
    setFsxFilesystems,
    setOutposts,
    setInstanceTags,
    setLoadingRegionData,
}) => {
    const { notifyError } = useNotificationContext();
    const hasPrefilledRef = useRef(false);

    useEffect(() => {
        if (hasPrefilledRef.current) {
            return;
        }
        if (!isRedeploy || !cloudAccount?.appliance || regions.length === 0) {
            return;
        }
        hasPrefilledRef.current = true;

        const appliance = cloudAccount.appliance;
        const props = appliance.cloud_properties;

        form.setFieldsValue({
            instanceType: props.instance_type,
            instanceName: props.vapp_name,
            encryptionEnabled: !!props.encrypt_volumes,
        });

        if (props.tags && typeof props.tags === 'object') {
            setInstanceTags(props.tags);
        }

        prefillCommonFromAppliance(appliance);

        if (appliance.vm_level_migration_enabled) {
            prefillVmLevelFields(appliance, form);
        }

        if (props.network) {
            const eth0 = props.network.interfaces?.eth0 || {};
            form.setFieldsValue({
                subnet: eth0.network_name,
                assignPublicIp: eth0.assign_public_ip ? 'enable' : 'disable',
                staticPrivateIp: eth0.ip_addr || '',
            });
        }

        if (props.region) {
            loadRedeployRegionData({
                appliance,
                cloudAccount,
                form,
                fetchAttributes,
                notifyError,
                setLoadingRegionData,
                setVPCs,
                setSubnets,
                setKmsKeys,
                setDedicatedHosts,
                setFsxFilesystems,
                setOutposts,
            });
        }
    }, [isRedeploy, cloudAccount, regions]);
};

